import { useEffect, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from '@mui/material';
import {
    SESSION_EXPIRED_EVENT,
    clearSessionExpiredPending,
} from '@/services/sessionExpired';

const COUNTDOWN_SEC = 10;
const DEFAULT_MSG = 'Your session has expired. Please log in again.';

/**
 * Shown when API returns SYSTEM_NOT_LOGIN (302). Confirm or 10s countdown ? /login.
 */
export default function SessionExpiredDialog() {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState(DEFAULT_MSG);
    const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SEC);
    const redirectedRef = useRef(false);

    const goLogin = () => {
        if (redirectedRef.current) return;
        redirectedRef.current = true;
        clearSessionExpiredPending();
        setOpen(false);
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.assign('/login');
        }
    };

    useEffect(() => {
        const handler = (_e: Event) => {
            if (typeof window !== 'undefined' && window.location.pathname === '/login') {
                clearSessionExpiredPending();
                return;
            }
            // Fixed English copy for session expiry (backend msg may be generic "Please log in first!")
            setMessage(DEFAULT_MSG);
            redirectedRef.current = false;
            setSecondsLeft(COUNTDOWN_SEC);
            setOpen(true);
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, handler as EventListener);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler as EventListener);
    }, []);

    useEffect(() => {
        if (!open) return;
        const t = window.setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    window.clearInterval(t);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => window.clearInterval(t);
    }, [open]);

    useEffect(() => {
        if (open && secondsLeft <= 0) {
            goLogin();
        }
    }, [open, secondsLeft]);

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            onClose={() => {
                // Only Confirm / countdown may leave � ignore backdrop click
            }}
            aria-labelledby="session-expired-title"
            aria-describedby="session-expired-desc"
        >
            <DialogTitle id="session-expired-title">Session Expired</DialogTitle>
            <DialogContent>
                <DialogContentText id="session-expired-desc" component="div">
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Redirecting to login in {secondsLeft}s...
                    </Typography>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={goLogin} autoFocus>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}
